// CertificateEditModal.jsx

import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Typography,
  Input,
  DatePicker,
  Grid,
  Select,
} from "antd";
import { LeftOutlined, CheckOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import RichTextEditor from "./RichTextEditor";
import { useAuth } from "../../services/authentication_API/authService";
import CertificatePreviewModal from "./CertificatePreviewModal";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// ---------------- TEMPLATE DEFINITIONS ----------------
// (YOUR TEMPLATE_DEFINITIONS REMAIN EXACTLY THE SAME – NO CHANGE)


const CertificateEditModal = ({ visible, onClose, templateType,appointmentData, isTemplateSelectionModalClose }) => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const { userData } = useAuth();
  // console.log("appointmentData in edit modal:", appointmentData);

  const patientsname = `${appointmentData.patient?.first_name} ${appointmentData.patient?.middle_name || ""} ${appointmentData.patient?.last_name}` ;
  // console.log("Patient's Name:", patientsname);
  const TEMPLATE_DEFINITIONS = {
  // --- TEMPLATE: Fit to fly ---
  'Fitness': {
    title: 'MEDICAL FITNESS CERTIFICATE',
    headerTitle: 'Fitness',
    fields: [
      { key: 'PatientName', label: "Patient's Name", type: 'text', initialValue: patientsname },
      { key: 'residentOf', label: "Resident Of", type: 'text', initialValue: '' },
      { key: 'Age', label: "Gestation Age", type: 'text', initialValue: `${appointmentData?.patient?.age || ""}` },
      { key: 'ParentName', label: "Parent's Name", type: 'text', initialValue: '' },
    ],
    textTemplate: (data) => `
      <p>I certify that I have examined ${data?.PatientName || "[Patient's Name]"}, ${data.Age || "[Age]"}, son of ${data.ParentName || "[Parent's Name]"} and resident of ${data.residentOf || "[Resident Of]"}.</p>
      <p> Based on the clinical examination, I certify that he is in normal state of health and free from any communicable or non-communicable disease/illness or physical defects/infirmity, which may interfere with his schooling including the active outdoor activities. </p>
      <p>The immunization status and records are up-to date as per Universal Immunization Programme (UIP)/ IAP Immunization Schedule</p>
    `,
  },
  'Leave after illness': {
    title: 'Fit to fly certificate',
    headerTitle: 'Fit to fly',
    fields: [
      { key: 'PatientName', label: "Patient's Name", type: 'text', initialValue: patientsname },
      { key: 'Diagnosis', label: "Diagnosis", type: 'text', initialValue: '' },
      { key: 'Start Date', label: "Start Date", type: 'date', initialValue: ''},
      { key: 'End Date', label: "End Date", type: 'date', initialValue: '' },
      { key: 'Join Date', label: "Join Date", type: 'date', initialValue: '' },

    ],
    textTemplate: (data) => `
      <p>This is to certify that ${data?.PatientName || "[Patient's Name]"} was suffering from ${data.Diagnosis || "[Diagnosis]"} and was advised medical leave from ${data['Start Date'] || "[Start Date]"} to ${data['End Date'] || "[End Date]"} . He is fit to join from ${data['Join Date'] || "[Join Date]"}</p>
    `,
  },
  'Fit to fly': {
    title: 'Fit to fly certificate',
    headerTitle: 'Fit to fly',
    fields: [
      { key: 'PatientName', label: "Patient's Name", type: 'text', initialValue: patientsname },
      { key: 'partnerName', label: "Partner's Name", type: 'text', initialValue: '' },
      { key: 'residentOf', label: "Resident Of", type: 'text', initialValue: '' },
      { key: 'gestationAge', label: "Gestation Age", type: 'text', initialValue: `${appointmentData?.patient?.age || ""}` },
      { key: 'expectedDateOfDelivery', label: "Expected Date of Delivery", type: 'date', initialValue: '' },
    ],
    textTemplate: (data) => `
      <p>This is to certify that ${data?.PatientName || "[Patient's Name]"}, ${data?.gestationAge || "[Age]"}, partner of ${data.partnerName || "[Partner's Name]"}, resident of ${data.residentOf || "[Resident Of]"} is ${data.gestationAge || "[Gestation Age]"} pregnant with expected date of delivery ${data.expectedDateOfDelivery || "[Expected Date of Delivery]" }.</p>
      <p>She is under my care during her pregnancy and is fit to fly.</p>
    `,
  },
  
  // --- TEMPLATE: Leave during illness ---
  'Leave during illness': {
    title: 'MEDICAL LEAVE CERTIFICATE',
    headerTitle: 'Leave during illness',
    fields: [
      { key: 'patientName', label: "Patient's Name", type: 'text', initialValue: patientsname },

      { key: 'diagnosis', label: "Diagnosis", type: 'text', initialValue: '' },
      { key: 'startDate', label: "Start Date", type: 'date', initialValue: '' },
      { key: 'endDate', label: "End Date", type: 'date', initialValue: '' },
    ],
    textTemplate: (data) => `
      <p>This is to certify that ${data.patientName || "[Patient's Name]"} is suffering from ${data.diagnosis || "[Diagnosis]"} and is advised medical leave from ${data?.startDate || "[Start Date]"} to ${data?.endDate || "[End Date]"}.</p>
    `,
  },  

  // --- TEMPLATE: Fitness w/o Immunization ---
  'Fitness w/o Immunization': {
    title: 'MEDICAL FITNESS CERTIFICATE',
    headerTitle: 'Fitness w/o Immunization',
    fields: [
      { key: 'patientsName', label: "Patient's Name", type: 'text', initialValue: patientsname },
      { key: 'parentName', label: "Parent Name", type: 'text', initialValue: '' },
      { key: 'DOB', label: "Date of Birth", type: 'date', initialValue: `${appointmentData?.patient?.date_of_birth || ""}` },


    ],
    textTemplate: (data) => `
      <p>I certify that I have examined <b>${data?.patientsName || "[Patient's Name]"}, son of ${data.parentName || "[Parent Name]"} ,${data.DOB || "[Date of Birth]"}</b></p>
      <p>Based on the clinical examination, I certify that he is in normal state of health and free from any communicable or non-communicable disease/illness or physical defects/infirmity, which may interfere with his schooling including the active outdoor activities.</p>
      <p>The immunization status and records are up-to date as per Universal Immunization Programme (UIP)/ IAP Immunization Schedule.</p>
    `,
  },
  
  // --- TEMPLATE: Discharge Summary ---
  'Discharge Summary': {
    title: 'Discharge Summary',
    headerTitle: 'Discharge Summary',
    fields: [
      { key: 'patientsName', label: "Patient's Name", type: 'text', initialValue: patientsname },
      { key: 'age', label: "Age", type: 'number', initialValue: `${appointmentData?.patient?.age || ""}` },
      { key: 'sex', label: "Sex", type: 'text', initialValue: `${appointmentData?.patient?.gender || ""}` },
      { key: 'residentOf', label: "Resident Of", type: 'text', initialValue: '' },
      { key: 'dateOfAdmission', label: "Date of admission", type: 'date', initialValue: '' },
      { key: 'dateOfDischarge', label: "Date of discharge", type: 'date', initialValue: '' },
      { key: 'incharge', label: "Incharge", type: 'text', initialValue: '' },
      { key: 'historyAndFindings', label: "History & Findings", type: 'text', initialValue: '' },
      { key: 'courseAndTreatment', label: "Course and Treatment", type: 'text', initialValue: '' },
      { key: 'advice', label: "Advice", type: 'text', initialValue: '' },
    ],
    textTemplate: (data) => `
      <p>This is to certify that ${data.patientsName || "[Patient's Name]"}, ${data.sex || "[Sex]"}, resident of ${data.residentOf || "[Resident Of]"} was admitted at the hospital on <b>${data.dateOfAdmission || "[Date of Admission]"}</b> and discharged on <b>${data.dateOfDischarge || "[Date of Discharge]"}</b>, Consultant incharge <b>${data.incharge || "[Incharge]"}</b>.</p>
      <p>Noted history and findings of the patient: <b>${data.historyAndFindings || "[History & Findings]"}</b>.</p>
      <p>Hospital Course and Treatment <b>${data.courseAndTreatment || "[Course and Treatment]"}</b>.</p>
      <p>Advice of discharge <b>${data.advice || "[Advice]"}</b>.</p>
    `,
  },

  // --- TEMPLATE: Metallic Implants ---
  'Metallic Implants': {
    title: 'To Whom It May Concern',
    headerTitle: 'Metallic Implants',
    fields: [
      { key: 'patientsName', label: "Patient's Name", type: 'text', initialValue: patientsname },
      { key: 'bodyPart1', label: "Body Part 1", type: 'text', initialValue: '' },
      { key: 'dateOfProcedure', label: "Date of Procedure", type: 'date', initialValue: '' },
      { key: 'bodyPart2', label: "Body Part 2", type: 'text', initialValue: '' },
    ],
    textTemplate: (data) => `
      <p>This is to certify that ${data.patientsName || "[Patient's Name]"}, has undergone an operation at his <b>${data.bodyPart1 || "[Body Part 1]"}</b> on <b>${data.dateOfProcedure || "[Date of Procedure]"}</b> by which some metallic implants have been inserted permanently at his <b>${data.bodyPart2 || "[Body Part 2]"}</b>.</p>
    `,
  },
  
  // --- TEMPLATE: Mother Leave ---
  'Mother Leave': {
    title: 'To Whom It May Concern',
    headerTitle: 'Mother Leave',
    fields: [
      { key: 'patientsName', label: "Patient's Name", type: 'text', initialValue:patientsname },
      { key: 'parentName', label: "Parent Name", type: 'text', initialValue: '' },
      { key: 'birthWeight', label: "Birth Weight", type: 'text', initialValue: '' },
      { key: 'dateOfBirth', label: "Date of Birth", type: 'date', initialValue: `${appointmentData?.patient?.date_of_birth || ""}` },
    ],
    textTemplate: (data) => `
      <p>This is to certify that <b>${data.patientsName || "[Patient's Name]"}</b>, Son of <b>${data.parentName || "[Parent Name]"}</b> was born on <b>${data.dateOfBirth || "[Date of Birth]"}</b> with birth weight <b>${data.birthWeight || "[Birth Weight]"} </b>.</p>
      <p>He has been on exclusive breast feed & is gaining weight appropriately. It is further advised that the baby should be on exclusive breast feed for another 6 months so that he can continue to gain weight appropriately.</p>
      <p>Hence it is recommended that the mother should be granted leave at least till the baby is of 6 months of age.</p>
    `,
  },

  // --- TEMPLATE: Fitness adult ---
  'Fitness adult': {
    title: 'MEDICAL FITNESS CERTIFICATE',
    headerTitle: 'Fitness adult',
    fields: [
      { key: 'dateOfBirth', label: "Date of Birth", type: 'date', initialValue: `${appointmentData?.patient?.date_of_birth || ""}` },
      { key: 'patientsName', label: "Patient's Name", type: 'text', initialValue: patientsname },

    ], // No variable fields shown in the image
    textTemplate: (data) => `
      <p>I certify that I have examined <b>${data.patientsName || "[Patient's Name]"}</b>, DOB <b>${data.dateOfBirth || "[Date of Birth]"}</b>.</p>
      <p>Based on the clinical examination, I certify that he is in normal state of health and free from any communicable or non-communicable disease/illness or physical defects/infirmity.</p>
    `,
  },
  
  // --- TEMPLATE: Swimming ---
  'Swimming': {
    title: 'SWIMMING FITNESS CERTIFICATE',
    headerTitle: 'Swimming',
    fields: [
      { key: 'patientsName', label: "Patient's Name", type: 'text', initialValue: patientsname },
    ], // No variable fields shown in the image
    textTemplate: (data) => `
      <p>This is to certify that <b>${data.patientsName || "[Patient's Name]"}</b> has been examined and he does not suffer from any contagious ailment, chronic skin disorder or epilepsy.</p>
      <p>He is not on any medications.</p>
      <p>He is physically fit for swimming.</p>
    `,
  },
  'Make your Own Form': {
    title: 'MAKE YOUR OWN FORM',
    headerTitle: 'Make your Own Form',
    fields: [      { key: 'patientsName', label: "Patient's Name", type: 'text', initialValue: patientsname },
], // No variable fields shown in the image
    textTemplate: (data) => `
      <p>This is to certify that <b>${data.patientsName || "[Patient's Name]"}</b> has been examined and he does not suffer from any contagious ailment, chronic skin disorder or epilepsy.</p>
      <p>He is not on any medications.</p>
      <p>He is physically fit for swimming.</p>
    `,
  },
};

  // ------------ NEW: Focus Mode State (for iPad fix) ------------
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const [isCertificateEditModalClose, setIsCertificateEditModalClose] = useState(false);

  // ------------ TEMPLATE SELECTION ------------
  const currentTemplate = useMemo(
    () =>
      TEMPLATE_DEFINITIONS[templateType?.name] || {
        fields: [],
        textTemplate: () => "Template not found.",
        headerTitle: "Error",
      },
    [templateType]
  );

  // ------------ INITIAL FORM DATA ------------
const initialData = useMemo(() => {
  // Extract template-specific fields
  const templateFields = currentTemplate.fields.reduce((acc, field) => {
    acc[field.key] = field?.initialValue || "";
    return acc;
  }, {});

  // Return the full object structure requested
  return {
    doctor_id: userData?.id, // Set from props/context
    appointment_id: appointmentData.appointmentId,
    patient_id: appointmentData.patient?.id,
    ct_type_id: templateType?.id || 0, // Assuming templateType has an id
    ct_type_name: templateType?.name || "",
    certificate_name: templateType?.name || "",
    fields: templateFields
  };
}, [currentTemplate, templateType]);

  const [formData, setFormData] = useState(initialData);
  const [editableText, setEditableText] = useState(() =>
    currentTemplate.textTemplate(initialData?.fields)
  );
  const [isCetificatePreview, setIsCetificatePreview] = useState(false);

  useEffect(() => {
    setFormData(initialData);
    setEditableText(currentTemplate.textTemplate(initialData?.fields));
  }, [currentTemplate, initialData]);

const handleFieldChange = (key, value) => {
  // console.log(`Field changed: ${key} = ${value}`);
  const updated = {
    ...formData,
    fields: {
      ...formData.fields,
      [key]: value
    }
  };
  setFormData(updated);
  // Pass only the fields object to the text template
  setEditableText(currentTemplate.textTemplate(updated.fields));
};
const handlePreview = () => {
  
  navigate("/preview-certificate", {
    state: { 
      title: currentTemplate.title, 
      text: editableText,
      payload: formData // Optional: pass the whole object
    },
  });
};

useEffect(() => {
  if( isCertificateEditModalClose )
  {
    onClose();
    isTemplateSelectionModalClose();
  }
}, [isCertificateEditModalClose]);

  // ------------ HEADER ------------
  const customHeader = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 24px",
        backgroundColor: "#00bfff",
        color: "white",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <LeftOutlined
          style={{ fontSize: "24px", cursor: "pointer", marginRight: "16px" }}
          onClick={onClose}
        />
        <Title level={4} style={{ color: "white", margin: 0 }}>
          {currentTemplate.headerTitle}
        </Title>
      </div>

      {!isEditorFocused ? <Button
        type="primary"
        ghost
        icon={<CheckOutlined />}
        style={{ color: "white", borderColor: "white" }}
        onClick={(e) => {
          setIsCetificatePreview(true);
        }}
        
      >
        Preview
      </Button> :
      <Button
        type="primary"
        ghost
        icon={<CheckOutlined />}
        style={{ color: "white", borderColor: "white" }}
        onClick={(e) =>{
          e.stopPropagation();
          setIsEditorFocused(false)}
        }
      >
        Done
      </Button>}
    </div>
  );

  // Render different inputs
  const getInputField = (field) => {
    const currentValue = formData.fields[field.key];
    switch (field.type) {
      case "date":
        return (
          <DatePicker
            size="large"
            // Use value, "DD-MM-YYYY") to ensure correct parsing
            value={currentValue ? dayjs(currentValue, "DD-MM-YYYY") : null}
            onChange={(d, ds) => handleFieldChange(field.key, ds)} // ds is already formatted per format prop
            format="DD-MM-YYYY"
            style={{ width: "100%" }}
          />
        );

      case "select":
        return (
          <Select
            size="large"
            value={currentValue}
            onChange={(v) => handleFieldChange(field.key, v)}
            options={field.options}
            style={{ width: "100%" }}
          />
        );

      default:
        return (
          <Input
            size="large"
            value={currentValue}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
          />
        );
    }
  };

  return (
    <>
      <Modal
        title={null}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={screens.lg ? 900 : "90%"}
        maskClosable={false}
        styles={{ body: { padding: 0 } }}
        closeIcon={null}
        style={{ top: 0 }}
      >
        {customHeader}

        <div style={{ padding: 24 }}>
          {/* ------------------ HIDE FIELDS WHEN EDITING ------------------ */}
          {!isEditorFocused && currentTemplate.fields.length > 0 && (
            <>
              <Row gutter={[24, 16]}>
                {currentTemplate.fields.map((field) => (
                  <Col xs={24} sm={12} md={8} key={field.key}>
                    <Text strong>{field.label}</Text>
                    {getInputField(field)}
                  </Col>
                ))}
              </Row>

              <hr style={{ margin: "30px 0" }} />
            </>
          )}

          {/* ------------------ HIDE TITLE WHEN EDITING ------------------ */}
          {!isEditorFocused && (
            <Title level={4} style={{ textAlign: "center", marginBottom: 20 }}>
              {currentTemplate.title}
            </Title>
          )}

          {/* ------------------ EDITOR ------------------ */}
          <RichTextEditor
            value={editableText}
            onChange={setEditableText}
            placeholder="Edit the certificate content here..."
            height={isEditorFocused ? "70vh" : 250}
            onFocus={() => setIsEditorFocused(false)}
            onBlur={() => setIsEditorFocused(false)}
          />
        </div>
      </Modal>

      {isCetificatePreview && (
        <CertificatePreviewModal
          openCetificatePreview={isCetificatePreview}
          onClose={() => setIsCetificatePreview(false)}
          cetificateTitle={currentTemplate.title}
          formData={formData}
          cetificatePreviewData={editableText}
          isCertificateEditModalClose={() => setIsCertificateEditModalClose(true)}
        />
      )}
    </>
  );
};

CertificateEditModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  templateType: PropTypes.object.isRequired,
  appointmentData: PropTypes.object.isRequired,
  isTemplateSelectionModalClose: PropTypes.func.isRequired,
};

export default CertificateEditModal;
